// src/pages/LoginPage.tsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const useAppVersion = (): string | null => {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    api.get<{ version?: string }>('/version')
      .then((r) => setVersion(r.data?.version ?? null))
      .catch(() => setVersion(null));
  }, []);
  return version;
};

type LoginErrorType = 'EMAIL_NOT_VERIFIED' | 'ACCOUNT_PENDING' | 'ACCOUNT_BLOCKED' | 'INVALID_PROFILE_CONFIGURATION' | 'INVALID_PASSWORD' | 'USER_NOT_FOUND' | 'INVALID_CREDENTIALS' | 'HCAPTCHA_REQUIRED' | 'HCAPTCHA_INVALID' | 'HCAPTCHA_UNAVAILABLE' | 'SERVER_ERROR' | 'GENERIC';

const ERROR_MESSAGES: Record<LoginErrorType, string> = {
  EMAIL_NOT_VERIFIED: 'Seu email ainda não foi verificado. Verifique sua caixa de entrada (e a pasta de spam) para encontrar o link de confirmação.',
  ACCOUNT_PENDING: 'Sua conta está aguardando aprovação do administrador. Você receberá um email quando for aprovado.',
  ACCOUNT_BLOCKED: 'Sua conta foi bloqueada. Entre em contato com o administrador do sistema.',
  INVALID_PROFILE_CONFIGURATION: 'O perfil Secretaria precisa estar vinculado a uma secretaria. Entre em contato com o administrador.',
  INVALID_PASSWORD: 'Senha incorreta. Verifique e tente novamente.',
  USER_NOT_FOUND: 'CPF/CNPJ não encontrado. Verifique os dados ou cadastre-se.',
  INVALID_CREDENTIALS: 'CPF/CNPJ ou senha inválidos. Verifique e tente novamente.',
  HCAPTCHA_REQUIRED: 'Confirme que você é uma pessoa antes de acessar o sistema.',
  HCAPTCHA_INVALID: 'A verificação de segurança expirou ou não foi aceita. Tente novamente.',
  HCAPTCHA_UNAVAILABLE: 'A verificação de segurança está indisponível. Tente novamente em instantes.',
  SERVER_ERROR: 'Erro interno no servidor. Tente novamente mais tarde.',
  GENERIC: 'Erro ao conectar ao servidor. Tente novamente mais tarde.',
};

const RESEND_COOLDOWN_SECONDS = 60;

type HCaptchaConfig = {
  enabled: boolean;
  siteKey?: string;
};

const LoginPage = () => {
  const appVersion = useAppVersion();
  const [document, setDocument] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorType, setErrorType] = useState<LoginErrorType | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [hCaptchaConfig, setHCaptchaConfig] = useState<HCaptchaConfig | null>(null);
  const [hCaptchaRequired, setHCaptchaRequired] = useState(false);
  const [hCaptchaToken, setHCaptchaToken] = useState('');
  const [hCaptchaLoadError, setHCaptchaLoadError] = useState(false);
  const captchaRef = useRef<HCaptcha>(null);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = (location.state as any)?.message as string | undefined;

  const isExpired = new URLSearchParams(location.search).get('expired') === 'true';
  const expiredMessage = isExpired ? 'Sua sessão expirou. Por favor, faça login novamente.' : null;

  useEffect(() => {
    return () => { if (cooldownTimer.current) clearInterval(cooldownTimer.current); };
  }, []);

  useEffect(() => {
    let active = true;
    api.get<HCaptchaConfig>('/auth/hcaptcha-config')
      .then(({ data }) => {
        if (!active) return;
        if (data.enabled && !data.siteKey) throw new Error('hCaptcha sitekey ausente');
        setHCaptchaConfig(data);
      })
      .catch(() => {
        if (active) setHCaptchaLoadError(true);
      });
    return () => { active = false; };
  }, []);

  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownTimer.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setResendSuccess(false);
    const cleanDocument = document.replace(/\D/g, '');
    try {
      await api.post('/users/resend-verification', { document: cleanDocument });
      setResendSuccess(true);
      startCooldown();
    } catch {
      // ainda mostra cooldown para evitar enumeração
      startCooldown();
    } finally {
      setResendLoading(false);
    }
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setHCaptchaRequired(false);
    setHCaptchaToken('');
    captchaRef.current?.resetCaptcha();
    if (value.length <= 11) {
      setDocument(value
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2'));
    } else {
      setDocument(value
        .slice(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2'));
    }
  };

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [successMessage, navigate, location.pathname]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorType(null);
    setResendSuccess(false);
    if (hCaptchaRequired && !hCaptchaToken) {
      setErrorType('HCAPTCHA_REQUIRED');
      return;
    }
    setLoading(true);
    const cleanDocument = document.replace(/\D/g, '');
    try {
      const success = await login(cleanDocument, password, hCaptchaToken);
      if (success) {
        navigate('/dashboard');
      } else {
        setErrorType('INVALID_CREDENTIALS');
      }
    } catch (err: any) {
      const code = err?.response?.data?.error as LoginErrorType | undefined;
      if (err?.response?.data?.captchaRequired || code === 'HCAPTCHA_REQUIRED') {
        setHCaptchaRequired(true);
      }
      if (code && code in ERROR_MESSAGES) {
        setErrorType(code);
      } else {
        setErrorType('GENERIC');
      }
    } finally {
      setLoading(false);
      setHCaptchaToken('');
      captchaRef.current?.resetCaptcha();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">

      {/* ── Header — igual à tela inicial ── */}
      <div className="flex flex-col items-center pt-14 pb-8 px-6 text-center">
        {/* Brasão */}
        <div className="w-24 h-24 rounded-full flex items-center justify-center mb-8 border-2 border-blue-400/40 shadow-2xl bg-white/5 overflow-hidden">
          <img
            src="favicon.ico"
            alt="Brasão de Sinop"
            className="w-20 h-20 object-contain drop-shadow-lg"
            onError={e => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='52' height='52' viewBox='0 0 24 24' fill='none' stroke='%2393c5fd' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'/%3E%3Cpolyline points='9 22 9 12 15 12 15 22'/%3E%3C/svg%3E";
            }}
          />
        </div>

        <p className="text-blue-300 text-sm font-semibold uppercase tracking-widest mb-3">
          Prefeitura Municipal de Sinop — MT
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-2">SIGEM</h1>
        <p className="text-blue-300 text-base font-medium mb-1">
          Sistema de Gestão de Emendas Municipais
        </p>
      </div>

      {/* ── Form ── */}
      <div className="flex-1 flex items-start justify-center px-4 pb-10">
        <div className="w-full max-w-sm">

          {/* Alerts */}
          {expiredMessage && (
            <div className="mb-4 flex items-start gap-3 p-3.5 bg-amber-900/40 border border-amber-500/30 rounded-xl text-amber-200 text-sm">
              <svg className="w-5 h-5 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              {expiredMessage}
            </div>
          )}
          {successMessage && (
            <div className="mb-4 flex items-start gap-3 p-3.5 bg-emerald-900/40 border border-emerald-500/30 rounded-xl text-emerald-200 text-sm">
              <svg className="w-5 h-5 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          )}
          {errorType && errorType !== 'EMAIL_NOT_VERIFIED' && (
            <div className="mb-4 flex items-start gap-3 p-3.5 bg-red-900/40 border border-red-500/30 rounded-xl text-red-200 text-sm">
              <svg className="w-5 h-5 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              {ERROR_MESSAGES[errorType]}
            </div>
          )}
          {errorType === 'EMAIL_NOT_VERIFIED' && (
            <div className="mb-4 p-3.5 bg-amber-900/40 border border-amber-500/30 rounded-xl text-amber-200 text-sm space-y-2">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                </svg>
                <span>{ERROR_MESSAGES.EMAIL_NOT_VERIFIED}</span>
              </div>
              {resendSuccess && (
                <p className="text-emerald-300 text-xs pl-8">Novo link enviado! Verifique seu email.</p>
              )}
              <div className="pl-8">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0 || resendLoading || !document}
                  className="text-xs font-medium text-amber-300 hover:text-amber-100 disabled:opacity-50 disabled:cursor-not-allowed underline underline-offset-2 transition"
                >
                  {resendLoading
                    ? 'Enviando...'
                    : resendCooldown > 0
                    ? `Reenviar email (aguarde ${resendCooldown}s)`
                    : 'Reenviar email de verificação'}
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* CPF / CNPJ */}
            <div>
              <label htmlFor="document" className="block text-sm font-medium text-slate-300 mb-1.5">
                CPF ou CNPJ
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-500">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input
                  type="text"
                  id="document"
                  value={document}
                  onChange={handleDocumentChange}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  maxLength={18}
                  className="w-full rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-600 bg-slate-800 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Senha */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-1.5">Senha</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-500">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  className="w-full rounded-xl pl-10 pr-11 py-3 text-sm text-white placeholder-slate-600 bg-slate-800 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-300 transition"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              <div className="flex justify-end mt-1.5">
                <Link
                  to="/forgot-password"
                  tabIndex={-1}
                  className="text-xs text-blue-400 hover:text-blue-300 transition"
                >
                  Esqueci minha senha
                </Link>
              </div>
            </div>

            {/* hCaptcha */}
            {hCaptchaConfig?.enabled && hCaptchaRequired && hCaptchaConfig.siteKey && (
              <div className="flex min-h-[78px] items-center justify-center">
                <HCaptcha
                  ref={captchaRef}
                  sitekey={hCaptchaConfig.siteKey}
                  theme="dark"
                  languageOverride="pt-BR"
                  onVerify={setHCaptchaToken}
                  onExpire={() => setHCaptchaToken('')}
                  onError={() => {
                    setHCaptchaToken('');
                    setErrorType('HCAPTCHA_UNAVAILABLE');
                  }}
                />
              </div>
            )}
            {!hCaptchaConfig && (
              <div className="flex min-h-[78px] items-center justify-center">
                {hCaptchaLoadError ? (
                  <p className="text-center text-sm text-red-300">
                    Não foi possível carregar a configuração de segurança.
                  </p>
                ) : (
                  <p className="text-sm text-slate-400">Carregando configuração de segurança...</p>
                )}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || hCaptchaLoadError || !hCaptchaConfig}
              className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all duration-200 text-base mt-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Entrando...
                </>
              ) : (
                <>
                  Acessar o Sistema
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-slate-400">
            Não tem uma conta?{' '}
            <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium transition">
              Cadastrar-se
            </Link>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <a
              href={`${window.location.pathname.startsWith('/emendas') ? '/emendas' : ''}/guia/guia-entidade.html`}
              className="flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 4h6a4 4 0 0 1 4 4v12a3 3 0 0 0-3-3H2z"/><path d="M22 4h-6a4 4 0 0 0-4 4v12a3 3 0 0 1 3-3h7z"/>
              </svg>
              Guia da entidade
            </a>
            <Link
              to="/contato"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
              </svg>
              Fale conosco
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-5 text-center text-slate-500 text-xs space-y-1">
        <div>&copy; {new Date().getFullYear()} SIGEM &mdash; Prefeitura Municipal de Sinop &mdash; MT &middot; Todos os direitos reservados</div>
        <div>Desenvolvido em Parceria com a Diretoria de Softwares e Suporte a TIC.</div>
        {appVersion && <div>Versão {appVersion}</div>}
      </div>

    </div>
  );
};

export default LoginPage;

